import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  bodyErrors,
  bodyTargetErrors,
  errorSchema,
  guardErrors,
  idParams,
  targetErrors,
} from '../common';
import {
  checklistGroupSchema,
  checklistItemSchema,
  checklistSchema,
} from '../entities';
import {
  checklistChildIdsInput,
  checklistItemsInput,
  nameInput,
} from '../inputs';

const c = initContract();

/**
 * The whole list, as create and edit both send it. The kind picks what comes
 * with the name: the items of a standard list, or the children of a combined
 * one. An edit must send the kind the list already has. Without a group, a new
 * list goes into the user's first one and an edited list stays where it is.
 */
export const checklistBody = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('standard'),
    name: nameInput,
    groupId: z.string().uuid().optional(),
    items: checklistItemsInput,
  }),
  z.object({
    kind: z.literal('combined'),
    name: nameInput,
    groupId: z.string().uuid().optional(),
    childIds: checklistChildIdsInput,
  }),
]);

/** Parsed body — items trimmed with blanks dropped, child ids deduplicated. */
export type ChecklistBody = z.infer<typeof checklistBody>;

/** Another of the user's lists already has this name, whatever its case or kind. */
const duplicateChecklist = { 409: errorSchema } as const;

const checklistGroupBody = z.object({ name: nameInput });

/** Every id of the set being ordered, in the new order. */
const checklistOrderBody = z.object({
  ids: z.array(z.string().uuid()),
});

/** Another of the user's groups already has this name, whatever its case. */
const duplicateChecklistGroup = { 409: errorSchema } as const;

const itemParams = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
});

export const checklistsContract = c.router(
  {
    /**
     * Every list, whole, in the order of the groups and then of the lists in
     * each — a combined list's children come from here too.
     */
    listChecklists: {
      method: 'GET',
      path: '/checklists',
      responses: { 200: z.array(checklistSchema), ...guardErrors },
    },

    getChecklist: {
      method: 'GET',
      path: '/checklists/:id',
      pathParams: idParams,
      responses: { 200: checklistSchema, ...targetErrors },
    },

    createChecklist: {
      method: 'POST',
      path: '/checklists',
      body: checklistBody,
      responses: {
        201: checklistSchema,
        ...bodyErrors,
        ...duplicateChecklist,
      },
    },

    /** The whole list: items or children replace what it had, ticks kept by id. */
    updateChecklist: {
      method: 'PUT',
      path: '/checklists/:id',
      pathParams: idParams,
      body: checklistBody,
      responses: {
        200: checklistSchema,
        ...bodyTargetErrors,
        ...duplicateChecklist,
      },
    },

    deleteChecklist: {
      method: 'DELETE',
      path: '/checklists/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    /** One tick, under the standard list that owns the item. */
    setChecklistItem: {
      method: 'PUT',
      path: '/checklists/:id/items/:itemId',
      pathParams: itemParams,
      body: z.object({ checked: z.boolean() }),
      responses: { 200: checklistItemSchema, ...bodyTargetErrors },
    },

    /** Unticks every item; for a combined list, every item of every child. */
    resetChecklist: {
      method: 'POST',
      path: '/checklists/:id/reset',
      pathParams: idParams,
      body: c.noBody(),
      responses: { 204: c.noBody(), ...targetErrors },
    },

    /** In the user's order. */
    listChecklistGroups: {
      method: 'GET',
      path: '/groups',
      responses: { 200: z.array(checklistGroupSchema), ...guardErrors },
    },

    /** Added at the end of the order. */
    createChecklistGroup: {
      method: 'POST',
      path: '/groups',
      body: checklistGroupBody,
      responses: {
        201: checklistGroupSchema,
        ...bodyErrors,
        ...duplicateChecklistGroup,
      },
    },

    renameChecklistGroup: {
      method: 'PATCH',
      path: '/groups/:id',
      pathParams: idParams,
      body: checklistGroupBody,
      responses: {
        200: checklistGroupSchema,
        ...bodyTargetErrors,
        ...duplicateChecklistGroup,
      },
    },

    /** 400 unless `ids` is exactly the user's groups — a stale list is refused. */
    reorderChecklistGroups: {
      method: 'PUT',
      path: '/groups/order',
      body: checklistOrderBody,
      responses: { 200: z.array(checklistGroupSchema), ...bodyErrors },
    },

    /** 400 unless `ids` is exactly the group's lists. */
    reorderChecklistGroupLists: {
      method: 'PUT',
      path: '/groups/:id/lists/order',
      pathParams: idParams,
      body: checklistOrderBody,
      responses: { 204: c.noBody(), ...bodyTargetErrors },
    },

    /** 409 while the group still holds a list. */
    deleteChecklistGroup: {
      method: 'DELETE',
      path: '/groups/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors, 409: errorSchema },
    },
  },
  { pathPrefix: '/checklists' },
);
